import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, from, Observable, retry, throwError } from "rxjs";
import { CommonService } from "app/services/common/common.service";
import { Constants } from "../constants/constants";
import { SnackbarTypes } from "app/models/snackbar.types";
import { StorageUtilsService } from "app/services/storage-utils/storage-utils.service";
import { Router } from "@angular/router";

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const httpInterceptor = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const _commonService = inject(CommonService);
  const _storageUtilsService = inject(StorageUtilsService);
  const token = _storageUtilsService.getAccessToken();
  const router = inject(Router);

  let modified = request;

  if (request.headers.has(Constants.avoidInterceptorHeader)) {
    // Pass the request through without intercepting it
    return next(request);
  }

  if (token) {
    modified = request.clone({
      setHeaders: {
        Authorization: "Bearer " + token,
      },
    });
  }

  function blobToJsonUsingFileReader(blob: Blob): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(JSON.parse(reader.result as string));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob); // Lire en tant que texte
    });
  }


  return next(modified).pipe(
    retry(0),
    catchError((error: HttpErrorResponse) => {
      if (modified.headers.has(Constants.avoidInterceptorErrorHandlerHeader)) {
        return throwError(() => error);
      }
      switch (error.status) {
        case 400: {
          if (error.error?.errorKey || error.error?.message) {
            showErrorSnackbar({
              errorMessage: _commonService.resolveApiErrorKey(error.error),
              commonService: _commonService,
            });
          } else if (error.error instanceof Blob && error.error.type === 'application/problem+json') {
            from(blobToJsonUsingFileReader(error.error))
              .pipe(
                catchError(() => throwError(() => 'errors.standardErrorMessage400'))
              )
              .subscribe(jsonError => {
                showErrorSnackbar({
                  errorMessage: _commonService.resolveApiErrorKey(jsonError),
                  commonService: _commonService,
                });
              });
          } else {
            showErrorSnackbar({
              errorMessage: 'errors.standardErrorMessage400',
              commonService: _commonService,
            });
          }
          break;
        }

        case 401: {
          if (error?.error?.error === Constants.invalidTokenError) {
            router.navigate([Constants.entryRoute, Constants.logoutRoute]);
          } else {
            showErrorSnackbar({
              errorMessage: "errors.standardErrorMessage401",
              commonService: _commonService,
            });
          }
          break;
        }

        case 403: {
          // if (!(request.url.split('/').includes('public'))) {
          // }
          if (error.error && error.error.errorKey) {
            showErrorSnackbar({
              errorMessage: "errors." + error.error.errorKey,
              commonService: _commonService,
            });
          } else {
            showErrorSnackbar({
              errorMessage: "errors.standardErrorMessage403",
              commonService: _commonService,
            });
          }
          break;
        }

        case 404: {
          if (!request.url.includes("/uaa/api/users/email/")) {
            showErrorSnackbar({
              errorMessage: "errors.standardErrorMessage404",
              commonService: _commonService,
            });
          }
          break;
        }

        case 405: {
          if (error.error && error.error.errorKey) {
            showErrorSnackbar({
              errorMessage: "errors." + error.error.errorKey,
              commonService: _commonService,
            });
          } else {
            showErrorSnackbar({
              errorMessage: "errors.standardErrorMessage405",
              commonService: _commonService,
            });
          }
          break;
        }

        case 500: {
          if (error.error && error.error.errorKey) {
            showErrorSnackbar({
              errorMessage: "errors." + error.error.errorKey,
              commonService: _commonService,
            });
          } else {
            showErrorSnackbar({
              errorMessage: "errors.standardErrorMessage500",
              commonService: _commonService,
            });
          }
          break;
        }

        case 502: {
          if (error.error && error.error.errorKey) {
            showErrorSnackbar({
              errorMessage: "errors." + error.error.errorKey,
              commonService: _commonService,
            });
          } else {
            showErrorSnackbar({
              errorMessage: "errors.standardErrorMessage502",
              commonService: _commonService,
            });
          }
          break;
        }

        case 504: {
          if (error.error && error.error.errorKey) {
            showErrorSnackbar({
              errorMessage: "errors." + error.error.errorKey,
              commonService: _commonService,
            });
          } else {
            showErrorSnackbar({
              errorMessage: "errors.standardErrorMessage504",
              commonService: _commonService,
            });
          }
          break;
        }

        default: {
          showErrorSnackbar({
            errorMessage: "errors.unknown_error",
            commonService: _commonService,
          });
          break;
        }
      }
      return throwError(error);
    }),
  );
};

const showErrorSnackbar = ({
  errorMessage,
  commonService,
}: {
  errorMessage: string;
  commonService?: CommonService;
}) => {
  commonService?.showSnackBar({
    message: errorMessage,
    snackBarType: SnackbarTypes.errorMessage,
  });
};
